import React, { useCallback, useEffect, useRef, useState } from "react";
import { IServerSideTableProps, PAGE_SIZE_OPTIONS } from "./definitions";
import {
  Box,
  Button,
  CollectionPreferences,
  Pagination,
  Select,
  SpaceBetween,
  Table,
  TableProps,
  TextFilter,
} from "@amzn/awsui-components-react";
import { intersection } from "lodash";

import Header from "@amzn/awsui-components-react/polaris/header";
import { useDebounce, useServerSideData } from "./hooks";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import styles from "./style.module.scss";
import { useNotifications } from "features/notifications";
import EmptyState from "../empty-state";
import { getMatchesCountText } from "utils/table-utils";

const ServerSideTable: React.FC<IServerSideTableProps> = ({
  columnDefinitions,
  saveWidths,
  queryHook,
  transformParams,
  transformResponse,
  tableTitle,
  loadingText = "Loading data...",
  selectionType,
  defaultPageSize,
  defaultSortingColumnIdx = 0,
  preferences,
  setPreferences,
  contentDisplayPreferenceOptions,
  pageSizeOptions,
  additionalFilters,
  onFilterChange,
  showSearchBar = true,
  skipQuery,
}: IServerSideTableProps) => {
  const { addNotification } = useNotifications();
  const errorRef = useRef<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [descendingSorting, setDescendingSorting] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filteringText, setFilteringText] = useState("");
  const debouncedFilteringText = useDebounce(filteringText, 300);
  const effectiveFilterText =
    debouncedFilteringText.length >= 2 ? debouncedFilteringText : "";
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<any>
  >(columnDefinitions[defaultSortingColumnIdx]);
  const { pageSize } = preferences ?? {};

  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: string;
  }>(() => {
    const initialFilters: {
      [key: string]: string;
    } = {};
    additionalFilters?.forEach((filter) => {
      if (filter.selectedOption.value) {
        initialFilters[filter.field] = filter.selectedOption.value;
      }
    });
    return initialFilters;
  });

  const handleFilterChange = useCallback(
    (field: string, selectedOption: OptionDefinition | null) => {
      setSelectedFilters((prev) => {
        const newFilters = {
          ...prev,
          [field]: selectedOption?.value ?? "",
        };
        onFilterChange?.(newFilters);
        return newFilters;
      });
    },
    [onFilterChange]
  );

  const {
    items,
    loading,
    totalCount,
    pagesCount,
    currentPageIndex: serverPageIndex,
    error,
  } = useServerSideData(
    queryHook,
    transformParams,
    {
      pagination: {
        currentPageIndex,
        pageSize,
      },
      sorting: {
        sortingColumn: {
          sortingField: sortingColumn.sortingField,
        },
        sortingDescending: descendingSorting,
      },
      filtering: {
        filteringText: effectiveFilterText,
        additionalFilters: selectedFilters,
      },
    },
    {
      defaultPageSize,
      transformResponse,
      skipQuery,
    }
  );

  useEffect(() => {
    setSelectedItems((oldSelected) => intersection(items, oldSelected));
  }, [items]);

  const onSortingChange: TableProps["onSortingChange"] = (event) => {
    setDescendingSorting(event.detail.isDescending ?? false);
    setSortingColumn(event.detail.sortingColumn);
  };

  const onClearFilter = () => {
    setFilteringText("");
  };

  useEffect(() => {
    if (error) {
      const errorMessage =
        "Failed to submit the request for querying items. Please contact Gaia team for issues.";

      if (errorMessage !== errorRef.current) {
        errorRef.current = errorMessage;
        addNotification({
          type: "error",
          header: errorMessage,
          dismissible: true,
          timeout: 10000,
          id: `error-${errorMessage}-${Date.now()}`,
        });
      }
    } else {
      errorRef.current = null;
    }
  }, [error, addNotification]);

  return (
    <Table
      enableKeyboardNavigation={true}
      loading={loading}
      selectedItems={selectedItems}
      items={items}
      onSortingChange={onSortingChange}
      onSelectionChange={(event) =>
        setSelectedItems(event.detail.selectedItems)
      }
      sortingColumn={sortingColumn}
      sortingDescending={descendingSorting}
      columnDefinitions={columnDefinitions}
      columnDisplay={preferences?.contentDisplay}
      selectionType={selectionType}
      variant="full-page"
      stickyHeader={true}
      resizableColumns={true}
      onColumnWidthsChange={saveWidths}
      wrapLines={preferences?.wrapLines}
      stripedRows={preferences?.stripedRows}
      contentDensity={preferences?.contentDensity}
      stickyColumns={preferences?.stickyColumns}
      header={
        <Header
          counter={
            !loading
              ? getHeaderCounterServerSideText(totalCount, selectedItems.length)
              : undefined
          }
        >
          {tableTitle}
        </Header>
      }
      loadingText={loadingText}
      empty={
        <EmptyState
          title="No matches"
          subtitle="We can't find a match."
          action={<Button onClick={onClearFilter}>Clear filter</Button>}
        />
      }
      filter={
        showSearchBar ? (
          <SpaceBetween size="l">
            <div className={styles.filterBoxWrapper}>
              <TextFilter
                className={styles.filterBox}
                filteringText={filteringText}
                onChange={({ detail }) =>
                  setFilteringText(detail.filteringText)
                }
                filteringAriaLabel="Filter items"
                filteringPlaceholder="Find items"
                filteringClearAriaLabel="Clear"
                countText={getMatchesCountText(totalCount)}
              />
              {additionalFilters?.map((filter) => (
                <Select
                  key={filter.field}
                  options={filter.options}
                  selectedOption={
                    selectedFilters[filter.field] !== undefined
                      ? filter.options.find(
                          (opt) => opt.value === selectedFilters[filter.field]
                        ) || filter.options[0]
                      : filter.selectedOption
                  }
                  onChange={(e) =>
                    handleFilterChange(filter.field, e.detail.selectedOption)
                  }
                />
              ))}
            </div>
          </SpaceBetween>
        ) : undefined
      }
      pagination={
        <Pagination
          pagesCount={pagesCount}
          currentPageIndex={serverPageIndex ?? 0}
          disabled={loading}
          onChange={(event) =>
            setCurrentPageIndex(event.detail.currentPageIndex)
          }
        />
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          disabled={false}
          preferences={preferences}
          onConfirm={({ detail }) => setPreferences(detail)}
          pageSizePreference={{
            options: pageSizeOptions ?? PAGE_SIZE_OPTIONS,
            title: "Page size",
          }}
          contentDisplayPreference={{
            options: contentDisplayPreferenceOptions || [],
            title: "Column preference",
          }}
          wrapLinesPreference={{
            label: "Wrap lines",
            description: "Select to see all the text and wrap the lines",
          }}
          stripedRowsPreference={{
            label: "Striped rows",
            description: "Select to shade every other row",
          }}
          contentDensityPreference={{
            label: "Compact mode",
            description: "Select to display compact mode",
          }}
          stickyColumnsPreference={{
            firstColumns: {
              title: "Stick first column(s)",
              description:
                "Keep the first column(s) visible while horizontally scrolling the table content.",
              options: [
                { label: "None", value: 0 },
                { label: "First column", value: 1 },
                { label: "First two columns", value: 2 },
              ],
            },
            lastColumns: {
              title: "Stick last column",
              description:
                "Keep the last column visible while horizontally scrolling the table content.",
              options: [
                { label: "None", value: 0 },
                { label: "Last column", value: 1 },
                { label: "Last two columns", value: 2 },
              ],
            },
          }}
        />
      }
    />
  );
};

export default ServerSideTable;

export const getHeaderCounterServerSideText = (
  totalCount: number,
  selectedCount: number | undefined
) => {
  return selectedCount && selectedCount > 0
    ? `(${selectedCount}/${totalCount})`
    : `(${totalCount})`;
};
